/**
 * DataProvider — Abstract base class defining the read interface.
 *
 * All data sources (blockchain, archive, wizard) implement this interface.
 * Components consume data through this interface and never know the source.
 */
class DataProvider {
  constructor() {
    if (new.target === DataProvider) {
      throw new Error('DataProvider is abstract and cannot be instantiated directly');
    }
  }

  /** @returns {'blockchain'|'archive'|'wizard'} */
  get sourceType() {
    throw new Error('sourceType must be implemented');
  }

  /** @returns {boolean} true for archive/wizard (no write operations available) */
  get isReadOnly() {
    throw new Error('isReadOnly must be implemented');
  }

  /**
   * Initialize the data source (connect to chain, load files, etc.)
   * @returns {Promise<{success: boolean, error?: string}>}
   */
  async initialize() {
    throw new Error('initialize() must be implemented');
  }

  /**
   * Get platform configuration (fees, curator, beneficiary, etc.)
   * @returns {Promise<Object>} Platform config merged with dynamic contract state
   */
  async getPlatformConfig() {
    throw new Error('getPlatformConfig() must be implemented');
  }

  /**
   * Get user role flags for a given account.
   * @param {string} account — Ethereum address
   * @returns {Promise<{userIsAdmin: boolean, userIsContractOwner: boolean, userIsPayee: boolean, userIsAuthorizedArtist: boolean, userIsProjectArtist: boolean}>}
   */
  async getUserRoles(account, projectId) {
    throw new Error('getUserRoles() must be implemented');
  }

  /**
   * Get all projects and their artist addresses.
   * @returns {Promise<{projects: Object[], artists: string[]}>}
   */
  async getProjects() {
    throw new Error('getProjects() must be implemented');
  }

  /**
   * Get detailed info for a specific project.
   * @param {number} projectId
   * @returns {Promise<Object>} Project details including prices, artist, payee, image URLs
   */
  async getProjectDetails(projectId) {
    throw new Error('getProjectDetails() must be implemented');
  }

  /**
   * Get all minted tokens for a project.
   * @param {number} projectId
   * @param {number} currentEditions — number of editions minted so far
   * @param {function} [onProgress] — optional callback for progress updates
   * @returns {Promise<Object[]>} Array of token objects {networkId, blockNumber, projectId, editionId, tokenId, transactionHash}
   */
  async getProjectTokens(projectId, currentEditions, onProgress) {
    throw new Error('getProjectTokens() must be implemented');
  }

  /**
   * Extract art script from a transaction hash.
   * @param {string} scriptTransactionHash
   * @returns {Promise<{script: string, poster: string, page: string, blockNumber: number|null}>}
   */
  async getProjectScript(scriptTransactionHash) {
    throw new Error('getProjectScript() must be implemented');
  }

  /**
   * Get all posts on a page (info, style, etc.) near the script block.
   * @param {string} pageName — the immutables.co page name
   * @param {number|null} scriptBlockNumber — block number of the script tx
   * @returns {Promise<Object[]>} Array of {poster, page, tag, content, blockNumber, transactionHash}
   */
  async getPagePosts(pageName, scriptBlockNumber) {
    return [];
  }

  /**
   * Get the owner of a specific token.
   * @param {number} tokenId
   * @returns {Promise<string|null>} Owner address or null
   */
  async getOwnerOfToken(tokenId) {
    throw new Error('getOwnerOfToken() must be implemented');
  }

  /**
   * Get event history for a specific edition/token.
   * @param {number} editionId
   * @param {Object[]} projectTokens — token array (needs tokenId and blockNumber)
   * @returns {Promise<Object[]>} Array of events
   */
  async getEditionEventHistory(editionId, projectTokens) {
    throw new Error('getEditionEventHistory() must be implemented');
  }

  /**
   * Get team members list (admin-only data).
   * @returns {Promise<Object>} Map of address → isTeammember
   */
  async getTeammembers() {
    throw new Error('getTeammembers() must be implemented');
  }

  /**
   * Get authorized artists list (admin-only data).
   * @returns {Promise<Object>} Map of address → isAuthorizedArtist
   */
  async getAuthorizedArtists() {
    throw new Error('getAuthorizedArtists() must be implemented');
  }

  /**
   * Get the contract's ETH balance.
   * @returns {Promise<{contractBalance: string, contractBalanceWei: string}>}
   */
  async getContractBalance() {
    throw new Error('getContractBalance() must be implemented');
  }

  /**
   * Fetch project-specific CSS from IPFS or other source.
   * @param {string} imageURLBase — IPFS base URL for the project
   * @param {boolean} isMobile — whether to fetch mobile styles
   * @returns {Promise<string|null>} CSS string or null
   */
  async getProjectCSS(imageURLBase, isMobile, projectId) {
    throw new Error('getProjectCSS() must be implemented');
  }
}

export default DataProvider;
